import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { Formik, Form } from "formik";
import Select from "react-select";

import { getAllZones, getWardsByZoneId } from "../../Apis/rfidApi";
import { useRfidStore } from "../Rfid/store/useRfidStore";
import {
  DEFAULT_REPORT_ZONE_ID,
  DEFAULT_REPORT_ZONE_NAME,
  useRfidReportsStore,
} from "./store/useRfidReportsStore";
import RfidReportsExpandableTable from "./RfidReportsExpandableTable";
import { RfidMapContent } from "../Rfid/RfidMapView";
import { ExportRfidReports } from "./ExportRfidReports";
import useGoogleMapsLoader from "../../../Hooks/useGoogleMapsLoader";

const RfidReports = () => {
  const [wards, setWards] = useState([]);
  const [viewMapRow, setViewMapRow] = useState(null);
  const [resolvedDefaultZoneId, setResolvedDefaultZoneId] = useState(
    DEFAULT_REPORT_ZONE_ID
  );

  useGoogleMapsLoader();

  const {
    reportRows,
    fetchRfidReports,
    updateFilter,
    resetFilters,
    filters,
  } = useRfidReportsStore();

  const { rfidCategories, assetTypes, fetchRfidCategories, fetchAssetTypes } =
    useRfidStore();

  useEffect(() => {
    const init = async () => {
      let zoneId = filters.zoneId || DEFAULT_REPORT_ZONE_ID;

      try {
        const zoneResponse = await getAllZones({ isActive: true });
        const activeZones = zoneResponse.filter((zone) => zone.isActive);

        const defaultZone =
          activeZones.find(
            (zone) => zone.zoneName === DEFAULT_REPORT_ZONE_NAME
          ) ||
          activeZones.find(
            (zone) => String(zone.zoneId) === DEFAULT_REPORT_ZONE_ID
          );
        zoneId = String(defaultZone?.zoneId ?? DEFAULT_REPORT_ZONE_ID);
        setResolvedDefaultZoneId(zoneId);
        updateFilter("zoneId", zoneId);

        await loadWards(zoneId);
      } catch (error) {
        console.error("Error fetching filter data:", error);
        await loadWards(zoneId);
      }

      fetchAssetTypes();
      fetchRfidCategories();
      fetchRfidReports({ zoneId, pageNumber: 1 });
    };

    init();
  }, []);

  const loadWards = async (zoneId) => {
    if (!zoneId) {
      setWards([]);
      return;
    }
    try {
      const response = await getWardsByZoneId(zoneId);
      setWards(response.filter((circle) => circle.isActive));
    } catch (error) {
      console.error("Error fetching wards:", error);
      setWards([]);
    }
  };

  const handleViewOnMap = (row) => {
    if (!row) return;
    const lat = Number(row?.latitude ?? row?.Latitude);
    const lng = Number(row?.longitude ?? row?.Longitude);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    setViewMapRow(row);
  };

  const closeViewMapModal = () => setViewMapRow(null);

  const initialValues = {
    circleId: filters.circleId || "",
    assetTypeId: filters.assetTypeId
      ? String(filters.assetTypeId)
      : "",
    assetCategoryId: filters.assetCategoryId
      ? String(filters.assetCategoryId)
      : "",
    fromDate: filters.fromDate || "",
    toDate: filters.toDate || "",
  };

  const applyReportFilters = (values) => {
    const zoneId =
      filters.zoneId || resolvedDefaultZoneId || DEFAULT_REPORT_ZONE_ID;
    Object.entries(values).forEach(([key, value]) => updateFilter(key, value));
    fetchRfidReports({
      ...values,
      zoneId,
      shift: "",
      pageNumber: 1,
    });
  };

  return (
    <>

      <div className="content text-start">
        <div className="page-content">

          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mt-3">
            <div>
              <h5 className="mb-1 fw-semibold">RFID REPORT Information</h5>
              <p className="text-muted mb-0">
                Monitor RFID scanning activities, vehicle movements, reader performance, and shift-wise operations across zones and wards.
              </p>
            </div>
            <div className="d-flex align-items-center flex-wrap">
              <ExportRfidReports rfidReportData={reportRows} />
            </div>
          </div>


          <div className="card shadow mt-3">
            <div className="card-body">
              <Formik
                initialValues={initialValues}
                enableReinitialize
                onSubmit={(values) => {
                  applyReportFilters(values);
                }}
              >
                {({ values, setFieldValue, handleSubmit }) => (
                  <>
                    <Form onSubmit={handleSubmit}>
                      <div className="row align-items-end">
                        <div className="col-md-3 mb-3">
                          <label className="form-label">Ward</label>
                          <Select
                            inputId="circleId"
                            name="circleId"
                            classNamePrefix="react-select"
                            options={wards.map((ward) => ({
                              value: String(ward.circleId),
                              label: ward.circleName,
                            }))}
                            value={
                              wards
                                .map((ward) => ({
                                  value: String(ward.circleId),
                                  label: ward.circleName,
                                }))
                                .find(
                                  (option) =>
                                    option.value === String(values.circleId)
                                ) || null
                            }
                            onChange={(selected) =>
                              setFieldValue("circleId", selected ? selected.value : "")
                            }
                            placeholder="Select Wards"
                            isClearable
                            menuPlacement="auto"
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                          />
                        </div>

                        <div className="col-md-3 mb-3">
                          <label className="form-label">Asset Type</label>
                          <Select
                            inputId="assetTypeId"
                            name="assetTypeId"
                            classNamePrefix="react-select"
                            options={assetTypes.map((type) => ({
                              value: String(type.assetTypeId ?? type.rfidTypeId ?? ""),
                              label: type.assetTypeName ?? type.rfidTypeName,
                            }))}
                            value={
                              assetTypes
                                .map((type) => ({
                                  value: String(
                                    type.assetTypeId ?? type.rfidTypeId ?? ""
                                  ),
                                  label: type.assetTypeName ?? type.rfidTypeName,
                                }))
                                .find(
                                  (option) =>
                                    option.value === String(values.assetTypeId)
                                ) || null
                            }
                            onChange={(selected) =>
                              setFieldValue(
                                "assetTypeId",
                                selected ? selected.value : ""
                              )
                            }
                            placeholder="Select Asset Type"
                            isClearable
                            menuPlacement="auto"
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                          />
                        </div>

                        <div className="col-md-3 mb-3">
                          <label className="form-label">Asset Category</label>
                          <Select
                            inputId="assetCategoryId"
                            name="assetCategoryId"
                            classNamePrefix="react-select"
                            options={rfidCategories.map((category) => ({
                              value: String(category.assetCategoryId),
                              label: category.assetCategoryName,
                            }))}
                            value={
                              rfidCategories
                                .map((category) => ({
                                  value: String(category.assetCategoryId),
                                  label: category.assetCategoryName,
                                }))
                                .find(
                                  (option) =>
                                    option.value === String(values.assetCategoryId)
                                ) || null
                            }
                            onChange={(selected) =>
                              setFieldValue(
                                "assetCategoryId",
                                selected ? selected.value : ""
                              )
                            }
                            placeholder="Select Asset Category"
                            isClearable
                            menuPlacement="auto"
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                          />
                        </div>

                        <div className="col-md-3 mb-3">
                          <label className="form-label">From Date &amp; Time</label>
                          <input
                            type="datetime-local"
                            name="fromDate"
                            value={values.fromDate}
                            onChange={(e) => setFieldValue("fromDate", e.target.value)}
                            className="form-control bg-white"
                          />
                        </div>

                        <div className="col-md-3 mb-3">
                          <label className="form-label">To Date &amp; Time</label>
                          <input
                            type="datetime-local"
                            name="toDate"
                            value={values.toDate}
                            min={values.fromDate || undefined}
                            onChange={(e) => setFieldValue("toDate", e.target.value)}
                            className="form-control bg-white"
                          />
                        </div>

                        <div className="col-md-3 mb-3">
                          <label
                            className="form-label"
                            aria-hidden="true"
                            style={{ visibility: "hidden" }}
                          >
                            &nbsp;
                          </label>
                          <div className="d-flex gap-2">
                            <button
                              type="submit"
                              style={{
                                backgroundColor: "#003366",
                                color: "#FFFFFF",
                                border: "none",
                                fontWeight: 600,
                                padding: "6px 16px",
                                borderRadius: "6px",
                                fontSize: "13px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              APPLY FILTERS
                            </button>
                            <button
                              type="button"
                              style={{
                                backgroundColor: "#FDE8E8",
                                color: "#8B1A1A",
                                border: "none",
                                fontWeight: 600,
                                padding: "6px 16px",
                                borderRadius: "6px",
                                fontSize: "13px",
                                whiteSpace: "nowrap",
                              }}
                              onClick={() => {
                                setResolvedDefaultZoneId(DEFAULT_REPORT_ZONE_ID);
                                resetFilters();
                                loadWards(DEFAULT_REPORT_ZONE_ID);
                              }}
                            >
                              RESET
                            </button>
                          </div>
                        </div>
                      </div>
                    </Form>

                    <div>
                      <div className="top-header text-center">
                        <span>RFID List</span>
                      </div>
                      <RfidReportsExpandableTable
                        rows={reportRows}
                        onViewMap={handleViewOnMap}
                      />
                    </div>
                  </>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={!!viewMapRow}
        onHide={closeViewMapModal}
        size="xl"
        centered
        aria-labelledby="rfid-reports-view-map-title"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title id="rfid-reports-view-map-title">View on Map</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 16 }}>
          {viewMapRow && (
            <RfidMapContent
              key={viewMapRow.assetRfidId ?? viewMapRow.rfidCode}
              row={viewMapRow}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default RfidReports;
